/*
 * Client tag presentation.
 *
 * A tag used to be painted with whatever hex the business picked out of a
 * nine-swatch palette. That put arbitrary saturated fills next to a product
 * layer that allows exactly one accent, and it made a tag's colour a decoration
 * rather than a signal.
 *
 * Now there are four operational tones and nothing else. A tag is always drawn
 * as an outlined pill in its tone — never as a filled field, and never from the
 * stored hex directly.
 *
 * Stored data is left exactly as it is. The hex on an existing row is read only
 * to decide which of the four tones it belongs to, so a business that has been
 * running a red "Allergy" tag since before this change keeps seeing it as a
 * danger tag without anyone rewriting the row.
 */

/** The four tones, in the order the picker offers them. */
export const TAG_TONES = {
  vip: {
    key: "vip",
    hex: "#0FA97B",
    label: "clients.tags.tones.vip",
    pill: "border-accent text-accent-text",
    selected: "border-accent bg-accent-wash text-accent-text",
    swatch: "bg-accent",
  },
  medical: {
    key: "medical",
    hex: "#C43D27",
    label: "clients.tags.tones.medical",
    pill: "border-danger text-danger-text",
    selected: "border-danger bg-danger-wash text-danger-text",
    swatch: "bg-danger",
  },
  noShow: {
    key: "noShow",
    hex: "#B4741A",
    label: "clients.tags.tones.noShow",
    pill: "border-warning text-warning-text",
    selected: "border-warning bg-warning-wash text-warning-text",
    swatch: "bg-warning",
  },
  neutral: {
    key: "neutral",
    hex: "#5A6A80",
    label: "clients.tags.tones.neutral",
    pill: "border-line-strong text-ink-2",
    selected: "border-line-strong bg-raised text-ink",
    swatch: "bg-line-strong",
  },
};

export const TAG_TONE_LIST = [
  TAG_TONES.vip,
  TAG_TONES.medical,
  TAG_TONES.noShow,
  TAG_TONES.neutral,
];

/** Hue and saturation only — lightness says nothing about which tone was meant. */
const hueSat = (hex) => {
  const match = /^#?([0-9a-f]{6})$/i.exec(String(hex ?? "").trim());
  if (!match) return null;

  const int = parseInt(match[1], 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const span = max - min;

  // A grey is a grey whatever its hue works out to.
  const saturation = span === 0 ? 0 : span / (255 - Math.abs(max + min - 255));
  if (span === 0) return { hue: 0, saturation: 0 };

  let hue;
  if (max === r) hue = ((g - b) / span) % 6;
  else if (max === g) hue = (b - r) / span + 2;
  else hue = (r - g) / span + 4;

  hue = Math.round(hue * 60);
  return { hue: hue < 0 ? hue + 360 : hue, saturation };
};

/**
 * Which tone a stored tag belongs to.
 *
 * The four tones' own hexes land on themselves, so a tag saved through the new
 * picker round-trips exactly. Everything else — the nine legacy swatches
 * included — falls into the nearest family by hue: reds are medical, ambers are
 * no-show, greens are VIP, and anything desaturated, blue, purple or pink is
 * neutral. Guessing a family beats flattening every historical tag to grey.
 */
export const toneFor = (tag) => {
  const hsl = hueSat(tag?.colour);
  if (!hsl || hsl.saturation < 0.2) return TAG_TONES.neutral;

  const { hue } = hsl;
  if (hue >= 340 || hue < 20) return TAG_TONES.medical;
  if (hue < 65) return TAG_TONES.noShow;
  if (hue < 175) return TAG_TONES.vip;
  return TAG_TONES.neutral;
};

/**
 * The pill classes for a tag.
 *
 * `selected` is for the places a tag is a toggle rather than a label — the
 * client filter row and the tag picker on a client. It keeps the outline and
 * adds a token wash plus weight, so "on" survives greyscale instead of riding
 * on colour alone.
 */
export const tagPillClass = (tag, { selected = false, interactive = false } = {}) => {
  const tone = toneFor(tag);
  return [
    "inline-flex items-center rounded-full border",
    selected ? `${tone.selected} font-bold` : tone.pill,
    interactive ? "transition-colors duration-fast" : "",
  ]
    .filter(Boolean)
    .join(" ");
};
